import {produce} from "immer";
import type { CheckoutCarRentalService } from '@/scopes/checkout/domain/types';
import type { FilterableCarItem } from '@/scopes/checkout/filterableCars/domain/types';

function formatDate(date: Date): string {
  const day = `0${date.getDate()}`.slice(-2);
  const month = `0${date.getMonth() + 1}`.slice(-2);
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
}

interface AddCarRentalServicePayload {
  participantId: string;
  value: string;
  id?: string;
  driverBirthDate: Date | null;
  bookedEquipmentIds: string[];
  bookedEquipmentsWithQuantity?: { id: string; quantity: number }[];
}

export function addCar(payload: AddCarRentalServicePayload) {
  return produce((service: CheckoutCarRentalService) => {
    const birthdate = payload.driverBirthDate ? formatDate(payload.driverBirthDate) : null;
    const optionId = payload.id ?? payload.value;

    service.carLists?.push({
      selectedValue: payload.value,
      selectedId: payload.id ?? '',
      driverId: payload.participantId,
      availableOptionsToUpgrade: [],
    });

    service.participants[payload.participantId].birthdate = birthdate;
    service.options[optionId].driverId = payload.participantId;
    service.options[optionId].selectedEquipments = payload.bookedEquipmentIds;
  });
}

export function editCar(payload: AddCarRentalServicePayload) {
  return produce((service: CheckoutCarRentalService) => {
    const birthdate = payload.driverBirthDate ? formatDate(payload.driverBirthDate) : null;
    const optionId = payload.id ?? payload.value;

    service.carLists =
      service.carLists?.map((carList) => {
        if (carList.selectedValue !== payload.value) {
          return carList;
        }
        return {
          ...carList,
          selectedValue: payload.value,
          selectedId: payload.id ?? '',
          driverId: payload.participantId,
          availableOptionsToUpgrade: [],
        };
      }) ?? [];

    service.participants[payload.participantId].birthdate = birthdate;
    service.options[optionId].driverId = payload.participantId;
    service.options[optionId].selectedEquipments = payload.bookedEquipmentIds;
    service.options[optionId].selectedEquipmentsWithQuantity = payload.bookedEquipmentsWithQuantity ?? [];
  });
}

interface RemoveCarRentalServicePayload {
  participantId: string;
}

export function removeCar(payload: RemoveCarRentalServicePayload) {
  return produce((service: CheckoutCarRentalService) => {
    const previousSelectedValue = service.participants[payload.participantId].selectedValue;
    const previousSelectedId = service.participants[payload.participantId].selectedId;

    if (!previousSelectedValue) return;
    service.carLists = service.carLists?.filter((car) => car.driverId !== payload.participantId) ?? [];
    service.options[previousSelectedId ?? previousSelectedValue].driverId = null;
    service.options[previousSelectedId ?? previousSelectedValue].selectedEquipments = [];
  });
}

interface UpgradeCarRentalServicePayload {
  participantId: string;
  value: string;
  id?: string;
}

export function upgradeCar(payload: UpgradeCarRentalServicePayload) {
  return produce((service: CheckoutCarRentalService) => {
    const previousSelectedValue = service.participants[payload.participantId].selectedValue;
    const previousSelectedId = service.participants[payload.participantId].selectedId;
    if (!previousSelectedValue) return;

    service.carLists =
      service.carLists?.map((car) => {
        if (car.driverId === payload.participantId) {
          return {
            ...car,
            selectedValue: payload.value,
            selectedId: payload.id ?? '',
          };
        }
        return car;
      }) ?? [];

    service.options[payload?.id ?? payload.value].driverId = payload.participantId;

    service.options[previousSelectedId ?? previousSelectedValue].selectedEquipments = [];
    service.options[previousSelectedId ?? previousSelectedValue].driverId = null;
  });
}



export function addCarToOptionAndUpgradeCar(payload: UpgradeCarRentalServicePayload) {
  return produce((service: CheckoutCarRentalService, cars: Record<string, FilterableCarItem>) => {
    const previousSelectedValue = service.participants[payload.participantId].selectedValue;
    const previousSelectedId = service.participants[payload.participantId].selectedId;
    if (!previousSelectedValue) return;

    service.carLists =
      service.carLists?.map((car) => {
        if (car.driverId === payload.participantId) {
          return {
            ...car,
            selectedValue: payload.value,
            selectedId: payload.id ?? '',
          };
        }
        return car;
      }) ?? [];

    if (!service.options[payload?.id ?? payload.value]) {
      service.options[payload?.id ?? payload.value] = {
        ...cars[payload?.id ?? payload.value],
        driverId: payload.participantId,
        selectedEquipments: [],
        selectedEquipmentsWithQuantity: [],
        canBeRemoved: false,
        canBeEdited: false,
        canBeUpgraded: false,
        availableOnRequest: false,
        maxNumberOfEquipments: 3,
        openJawFeeAmount: 0,
        isIncluded: false,
        location: {
          text: '',
          icon: "CarRight",
        },
        equipments: [],
      }
    }

    service.options[payload?.id ?? payload.value].driverId = payload.participantId;

    service.options[previousSelectedId ?? previousSelectedValue].selectedEquipments = [];
    service.options[previousSelectedId ?? previousSelectedValue].driverId = null;
  });
}
